import type { ActionId, WeaponId } from '@cratered/shared';

/**
 * move: the stamina budget. throw: drag to aim, flies. hitscan: instant ray. melee: the boot.
 * use: instant gear that acts where the mole stands (no aiming). skip: end the turn.
 */
export type ActionKind = 'move' | 'throw' | 'hitscan' | 'melee' | 'use' | 'skip';

/** What a thrown thing turns into when it comes to rest (or its fuse runs out). */
export type Deploy = 'mine' | 'hook';
/** Aimed like a throw but nothing flies: something is set down (or the mole moves) where the aim points. */
export type Place = 'platform' | 'charge' | 'self';

export interface ActionDef {
  id: ActionId;
  name: string;
  short: string;
  kind: ActionKind;
  /** Starting ammo per team. Infinity for unlimited; bought things start at 1 (one of everything is free). */
  ammo: number;
  damage: number;
  /** Blast radius for things that explode; 0 for direct-only hits. */
  radius: number;
  /** How hard a hit shoves the victim (world px per tick at point blank). */
  knockback: number;
  /** Launch speed at full power, px per tick (throw actions). */
  speed: number;
  gravity: number;
  fuseTicks?: number;
  bounce?: number;
  /** Max bounces before a non-exploding projectile dies. */
  maxBounces?: number;
  /** Melee reach in px. */
  reach?: number;
  /** Headshot damage multiplier for hitscan weapons. */
  headshot?: number;
  /** Hitscan: how far the shot carries (unlimited when unset). Damage fades to 40% at the end of it. */
  range?: number;
  /** Hitscan: drag whoever it hits this many px toward the shooter. */
  pull?: number;
  /** Throw: the drag sets the angle only; the thing always leaves at full speed. */
  fixedPower?: boolean;
  /** Aimed like a throw but nothing flies: digs a tunnel along the aim instead. */
  dig?: boolean;
  /**
   * Aimed like a throw but nothing flies: a platform (or a bridge, see `span`) is set down where
   * the aim points within PLACE_REACH; a charge is set down there and its fuse starts; `self`
   * blinks the mole there (within TELEPORT_REACH).
   */
  place?: Place;
  /** Width of the plank a 'platform' placement sets down (PLATFORM_W when unset). */
  span?: number;
  /** Throw: sticks to the first thing it touches (ground, deck or mole) and goes off on its fuse. */
  stick?: boolean;
  /** Throw: nothing is thrown; this many bombs fall from the sky onto the marked spot (see STRIKE_REACH). */
  strike?: number;
  /** Hitscan: this many rounds in a fan this wide (radians), each doing `damage`. */
  pellets?: number;
  spread?: number;
  /** Hitscan: the shot goes through ground, decks and moles, hurting everyone in the line. */
  pierce?: boolean;
  /** Only usable standing beside a catapult; the boulders come from its pile, not the team's stock. */
  station?: boolean;
  /** Throw: what it becomes when it stops or its fuse ends. */
  deploy?: Deploy;
  /** Explodes into this many bomblets. */
  split?: number;
  /**
   * Gear: using it takes the turn's move (like walking) instead of ending the turn, so a mole can
   * grapple across and still throw, or wall itself in and still shoot.
   */
  gear?: boolean;
  pveOnly?: boolean;
  hint: string;
}

/** Bought things start at one each (three grapples, two platforms and two barriers): every team gets a taste of the whole arsenal for free. */
const ONE = 1;

export const ACTIONS: Record<ActionId, ActionDef> = {
  move: {
    id: 'move', name: 'Move', short: 'MOVE', kind: 'move', ammo: Infinity, damage: 0, radius: 0, knockback: 0, speed: 0, gravity: 0,
    hint: 'Walk with ← → and jump with ↑ until the stamina bar runs out. Attacking still ends the turn.',
  },
  // Throw tuning: max range is speed² / gravity at 45°. Teams start 1100–2100 px apart, so a full
  // power throw must carry ~1600 px (Raft Wars: the far raft is always reachable).
  // The ball is light: it flies faster and farther than a grenade, and shoves harder the faster it arrives.
  ball: {
    id: 'ball', name: 'Ball', short: 'BALL', kind: 'throw', ammo: Infinity, damage: 12, radius: 0, knockback: 8, speed: 27,
    gravity: 0.28, bounce: 0.5, maxBounces: 2, hint: 'A tennis ball. Free. Little damage, big shove — the harder the throw, the bigger the shove.',
  },
  grenade: {
    id: 'grenade', name: 'Grenade', short: 'NADE', kind: 'throw', ammo: ONE, damage: 45, radius: 80, knockback: 13, speed: 22,
    gravity: 0.3, fuseTicks: 180, bounce: 0.5, hint: 'Bounces for three seconds, then sends everyone flying.',
  },
  // Heavier: a shorter, fatter arc and a crater you could park a boat in.
  bignade: {
    id: 'bignade', name: 'Big grenade', short: 'BIG', kind: 'throw', ammo: ONE, damage: 70, radius: 130, knockback: 16, speed: 22,
    gravity: 0.32, fuseTicks: 180, bounce: 0.35, hint: 'Twice the blast, half the bounce. Three second fuse. Mind the crater.',
  },
  cluster: {
    id: 'cluster', name: 'Cluster bomb', short: 'CLSTR', kind: 'throw', ammo: ONE, damage: 10, radius: 30, knockback: 6, speed: 22,
    gravity: 0.3, fuseTicks: 70, bounce: 0.3, split: 5, hint: 'Pops on the first bounce into five bomblets that scatter and go off a moment later.',
  },
  // Lobbed like a grenade, then sits where it stops. Arms when the turn ends; any enemy that comes
  // close sets it off, and so does a nearby blast.
  mine: {
    id: 'mine', name: 'Mine', short: 'MINE', kind: 'throw', ammo: ONE, damage: 50, radius: 90, knockback: 14, speed: 19,
    gravity: 0.32, fuseTicks: 110, bounce: 0.25, deploy: 'mine', hint: 'Lands and arms itself. Goes off when an enemy walks up to it, or when something blows up nearby.',
  },
  // Hitscan shoves stay under the ragdoll threshold: bullets wound, thrown things launch.
  // The free gun reaches about a raft and a half: a round should be won with throws, swings and
  // sniper rounds, not plinked away from the far shore.
  gun: {
    id: 'gun', name: 'Pistol', short: 'GUN', kind: 'hitscan', ammo: Infinity, damage: 16, radius: 0, knockback: 2.5, speed: 0, gravity: 0,
    headshot: 1.5, range: 700, hint: 'Free. Instant shot where the crosshair points, fading past 700 px. Headshots do 1.5×.',
  },
  // The exception: a shotgun at arm's length is a boot with a bang.
  shotgun: {
    id: 'shotgun', name: 'Shotgun', short: 'SHOT', kind: 'hitscan', ammo: ONE, damage: 36, radius: 0, knockback: 10, speed: 0, gravity: 0,
    headshot: 1.5, range: 340, hint: 'Short range, big shove: blows whoever is close off the raft. Fades past 340 px.',
  },
  sniper: {
    id: 'sniper', name: 'Sniper', short: 'SNPR', kind: 'hitscan', ammo: ONE, damage: 45, radius: 0, knockback: 3.5, speed: 0, gravity: 0,
    headshot: 1.5, hint: 'One precise shot across the whole map. Headshots do 1.5×.',
  },
  harpoon: {
    id: 'harpoon', name: 'Harpoon', short: 'HARP', kind: 'hitscan', ammo: ONE, damage: 18, radius: 0, knockback: 0, speed: 0, gravity: 0,
    headshot: 1, range: 1000, pull: 260, hint: 'Spears an enemy and yanks them 260 px toward you. Over water, that is a swim.',
  },
  boot: {
    id: 'boot', name: 'Boot', short: 'BOOT', kind: 'melee', ammo: Infinity, damage: 14, radius: 0, knockback: 13, speed: 0, gravity: 0,
    reach: 64, hint: 'Kick an enemy standing next to you. Pits and water do the rest.',
  },
  // Close quarters, bought: a bat for distance, a mallet for damage, a pike for reach.
  bat: {
    id: 'bat', name: 'Bat', short: 'BAT', kind: 'melee', ammo: ONE, damage: 22, radius: 0, knockback: 18, speed: 0, gravity: 0,
    reach: 72, hint: 'A big swing that sends whoever is next to you flying flat and far. Home run.',
  },
  mallet: {
    id: 'mallet', name: 'Mallet', short: 'MLLT', kind: 'melee', ammo: ONE, damage: 38, radius: 0, knockback: 3, speed: 0, gravity: 0,
    reach: 60, hint: 'Hammers a neighbour for 38 and cracks the raft panel under their feet. No launch.',
  },
  pike: {
    id: 'pike', name: 'Pike', short: 'PIKE', kind: 'melee', ammo: ONE, damage: 20, radius: 0, knockback: 9, speed: 0, gravity: 0,
    reach: 120, hint: 'A long poke: reaches twice as far as a boot and shoves them back.',
  },
  // A shell lobbed high that bursts where it lands: the big-blast arc weapon.
  mortar: {
    id: 'mortar', name: 'Mortar', short: 'MRTR', kind: 'throw', ammo: ONE, damage: 60, radius: 95, knockback: 14, speed: 24,
    gravity: 0.36, hint: 'A shell lobbed high that bursts where it comes down. Big blast, no bounce.',
  },
  rpg: {
    id: 'rpg', name: 'RPG', short: 'RPG', kind: 'throw', ammo: ONE, damage: 60, radius: 70, knockback: 14, speed: 26,
    gravity: 0.1, hint: 'Fast and nearly flat. Explodes on anything it touches.',
  },
  // A burst: five rounds in a loose fan. Each one wounds a little; together they punish a clear line.
  smg: {
    id: 'smg', name: 'SMG', short: 'SMG', kind: 'hitscan', ammo: ONE, damage: 9, radius: 0, knockback: 1.5, speed: 0, gravity: 0,
    headshot: 1.5, range: 650, pellets: 5, spread: 0.1, hint: 'A burst of five rounds in a loose fan, 9 each, fading past 650 px. Forgives a shaky aim.',
  },
  // Sticks to whatever it first touches: a wall, a deck, or a mole, who then carries it.
  sticky: {
    id: 'sticky', name: 'Sticky bomb', short: 'STCK', kind: 'throw', ammo: ONE, damage: 50, radius: 85, knockback: 13, speed: 23,
    gravity: 0.3, fuseTicks: 150, bounce: 0, stick: true, hint: 'Sticks to the first thing it touches, mole included, and goes off two and a half seconds later.',
  },
  // Four seconds of ricochets: down a tunnel, round a corner, off a ceiling.
  rubber: {
    id: 'rubber', name: 'Rubber bomb', short: 'RUBR', kind: 'throw', ammo: ONE, damage: 40, radius: 75, knockback: 12, speed: 24,
    gravity: 0.3, fuseTicks: 240, bounce: 0.85, hint: 'Bounces hard for four seconds, off walls, ceilings and moles, then pops where it is.',
  },
  // Mark a spot; bombs fall on it from the sky. Nothing to throw over, but a roof or a ceiling catches them.
  airstrike: {
    id: 'airstrike', name: 'Airstrike', short: 'AIR', kind: 'throw', ammo: ONE, damage: 32, radius: 70, knockback: 10, speed: 0,
    gravity: 0.3, strike: 5, hint: 'Drag to mark a spot (farther drag, farther spot): five bombs fall on it from the sky. Roofs and ceilings catch them.',
  },
  // The big one: 400 coins. A crater you could sink a barge in; the thrower is not safe either.
  massive: {
    id: 'massive', name: 'Massive bomb', short: 'MASS', kind: 'throw', ammo: ONE, damage: 140, radius: 250, knockback: 28, speed: 23,
    gravity: 0.36, fuseTicks: 300, bounce: 0.2, hint: 'A huge bomb on a five second fuse: 250 px blast, 140 damage, a crater that changes the map. Costs 400. Mind your own feet.',
  },
  // A bolt: no blast, so it only hurts what it hits, but it hits hard and flies flat.
  crossbow: {
    id: 'crossbow', name: 'Crossbow', short: 'BOLT', kind: 'throw', ammo: ONE, damage: 42, radius: 0, knockback: 6, speed: 34,
    gravity: 0.12, hint: 'A fast, flat bolt that only hurts what it hits: 42, more to the head. No blast.',
  },
  // Through everything: ground, decks, walls and anyone in the line.
  railgun: {
    id: 'railgun', name: 'Railgun', short: 'RAIL', kind: 'hitscan', ammo: ONE, damage: 55, radius: 0, knockback: 4, speed: 0, gravity: 0,
    headshot: 1.5, pierce: true, hint: 'A slug that goes straight through hills, rafts and moles, hurting every enemy in the line. No cover from this one.',
  },
  // Set down within reach and lit: a demolition charge for whoever (or whatever) is right there.
  tnt: {
    id: 'tnt', name: 'TNT', short: 'TNT', kind: 'throw', ammo: ONE, damage: 95, radius: 120, knockback: 18, speed: 0, gravity: 0.3,
    fuseTicks: 110, bounce: 0, place: 'charge', hint: 'Set a charge down within reach; it blows two seconds later, hard. Ends the turn, so keep it at arm’s length.',
  },
  // The canyon maps' emplacements: climb in and get flung across the gap. Three launches per
  // catapult per round; it takes the move, so you land and still get your attack.
  catapult: {
    id: 'catapult', name: 'Catapult', short: 'CATA', kind: 'throw', ammo: Infinity, damage: 0, radius: 0, knockback: 0, speed: 24,
    gravity: 0.42, station: true, gear: true,
    hint: 'Stand beside a catapult, climb in, aim and let go: it flings you across the map. No fall damage. Three launches per catapult per round; takes your move.',
  },

  // ---- Gear: takes the move, not the turn ----
  grapple: {
    id: 'grapple', name: 'Grapple hook', short: 'HOOK', kind: 'throw', ammo: 3, damage: 0, radius: 0, knockback: 0, speed: 30,
    gravity: 0.05, fixedPower: true, deploy: 'hook', gear: true,
    hint: 'Fires a hook. Where it bites, you follow. Takes your move; you can still attack after.',
  },
  platform: {
    id: 'platform', name: 'Platform', short: 'PLAT', kind: 'throw', ammo: 2, damage: 0, radius: 0, knockback: 0, speed: 0,
    gravity: 0, place: 'platform', gear: true,
    hint: 'Set a plank down where you point, within reach. Hangs in the air, floats on water, bridges a gap. Takes your move.',
  },
  bridge: {
    id: 'bridge', name: 'Bridge', short: 'BRDG', kind: 'throw', ammo: ONE, damage: 0, radius: 0, knockback: 0, speed: 0,
    gravity: 0, place: 'platform', span: 320, gear: true,
    hint: 'A long plank (320 px) set down where you point, within reach: spans a whole gap. Takes your move.',
  },
  teleport: {
    id: 'teleport', name: 'Teleport', short: 'TELE', kind: 'throw', ammo: ONE, damage: 0, radius: 0, knockback: 0, speed: 0,
    gravity: 0, place: 'self', gear: true,
    hint: 'Blink to where you point, up to 520 px, through walls. Refused into solid ground. Takes your move; you can still attack after.',
  },
  sandbags: {
    id: 'sandbags', name: 'Barrier', short: 'WALL', kind: 'use', ammo: 2, damage: 0, radius: 0, knockback: 0, speed: 0, gravity: 0,
    gear: true, hint: 'Stacks a wall in front of you. Stops bullets and balls until something blows it apart. Takes your move.',
  },
  medkit: {
    id: 'medkit', name: 'Medkit', short: 'MED', kind: 'use', ammo: ONE, damage: 0, radius: 0, knockback: 0, speed: 0, gravity: 0,
    gear: true, hint: 'Heals this mole 40 HP. Takes your move.',
  },
  repair: {
    id: 'repair', name: 'Boat repair', short: 'FIX', kind: 'use', ammo: ONE, damage: 0, radius: 0, knockback: 0, speed: 0, gravity: 0,
    gear: true, hint: 'Mends the boat you stand on: broken panels, worn pontoons, the hull of a vessel. Takes your move.',
  },
  pickaxe: {
    id: 'pickaxe', name: 'Pickaxe', short: 'PICK', kind: 'throw', ammo: ONE, damage: 0, radius: 0, knockback: 0, speed: 0, gravity: 0,
    fixedPower: true, dig: true, gear: true,
    hint: 'Digs a mole-sized tunnel along the aim: through a hill, into cover, or straight down. Takes your move.',
  },
  skip: {
    id: 'skip', name: 'Skip', short: 'SKIP', kind: 'skip', ammo: Infinity, damage: 0, radius: 0, knockback: 0, speed: 0, gravity: 0,
    hint: 'End the turn without attacking.',
  },
};

/** One of the cluster bomb's bomblets. */
export const BOMBLET: ActionDef = {
  id: 'cluster', name: 'Bomblet', short: 'BMB', kind: 'throw', ammo: 0, damage: 30, radius: 60, knockback: 9, speed: 0, gravity: 0.3,
  fuseTicks: 50, bounce: 0.35, hint: '',
};

/** Medkit heal, HP. */
export const MEDKIT_HEAL = 40;
/** How far a teleport blinks at a full drag. */
export const TELEPORT_REACH = 520;
/** How far out an airstrike can be marked at a full drag, and how far apart its bombs land. */
export const STRIKE_REACH = 1800;
export const STRIKE_GAP = 48;
/** A catapult: how close a mole must stand to climb in, its launches per round, and how high the cup sits. */
export const CATAPULT_REACH = 96;
export const CATAPULT_BOULDERS = 3;
export const CATAPULT_CUP_H = 74;
/** A platform, world px, and how far from the mole it can be set down (a full drag's worth). */
export const PLATFORM_W = 150;
export const PLATFORM_H = 16;
export const PLACE_REACH = 260;
/** A sandbag wall, world px, standing this far in front of the mole. */
export const SANDBAG_W = 24;
export const SANDBAG_H = 60;
export const SANDBAG_GAP = 42;
/** How close an enemy must come to set a mine off, and how long it beeps first. */
export const MINE_TRIGGER = 40;
export const MINE_FUSE = 40;
/** Px per tick a hooked mole (or a harpooned enemy) travels along the line. */
export const PULL_SPEED = 9;
/** A pickaxe tunnel: this many bites of this radius, this far apart, along the aim. */
export const DIG_BITES = 5;
export const DIG_RADIUS = 30;
export const DIG_STEP = 26;

/** Order in the action menu: free things first, then bought attacks, then gear. */
export const MENU_ORDER: readonly ActionId[] = [
  'move', 'ball', 'gun', 'boot', 'bat', 'mallet',
  'pike', 'grenade', 'bignade', 'sticky', 'rubber', 'cluster', 'mine', 'tnt',
  'shotgun', 'smg', 'sniper', 'railgun', 'harpoon', 'crossbow', 'mortar', 'rpg', 'airstrike', 'massive',
  'catapult', 'grapple', 'teleport', 'platform', 'bridge', 'sandbags', 'medkit', 'repair', 'pickaxe', 'skip',
];
export const WEAPON_IDS: readonly WeaponId[] = [
  'ball', 'grenade', 'bignade', 'cluster', 'mine', 'gun', 'shotgun', 'sniper', 'harpoon', 'boot', 'bat', 'mallet', 'pike', 'mortar', 'rpg',
  'smg', 'sticky', 'rubber', 'airstrike', 'massive', 'crossbow', 'railgun', 'tnt', 'catapult',
  'grapple', 'platform', 'bridge', 'teleport', 'sandbags', 'medkit', 'repair', 'pickaxe',
];

/** How hard a mallet hits the raft panel under its victim (panels have 100 HP). */
export const MALLET_PANEL_DAMAGE = 60;

export function startingAmmo(): Record<WeaponId, number> {
  const out = {} as Record<WeaponId, number>;
  for (const id of WEAPON_IDS) out[id] = ACTIONS[id].ammo;
  return out;
}

export function zeroAmmo(): Record<WeaponId, number> {
  const out = {} as Record<WeaponId, number>;
  for (const id of WEAPON_IDS) out[id] = 0;
  return out;
}

export function isWeapon(action: ActionId): action is WeaponId {
  return action !== 'move' && action !== 'skip';
}

/** Gear takes the move; everything else that is not Move or Skip ends the turn. */
export function isGear(action: ActionId): boolean {
  return !!ACTIONS[action].gear;
}

/**
 * Launch speed for a throw at `power` (0..1). The sim, the bots and the aim preview all use this,
 * so what the dotted arc shows is what flies. A minimum-power lob still travels ~300 px.
 */
export function launchSpeed(def: ActionDef, power: number, multiplier = 1): number {
  if (def.fixedPower) return def.speed * multiplier;
  return def.speed * (0.35 + 0.65 * power) * multiplier;
}
